/**
 * What Varza is allowed to do with no connection.
 *
 * Both lists below are allowlists, and that direction matters. A reader added to the API is
 * not silently copied onto devices, and a write added to the API is not silently queued and
 * replayed later; each has to be considered and named here first.
 */

/** An opaque path segment: an identifier, a slug, a code. */
const SEGMENT = '[^/?]+';

function route(pattern: string): RegExp {
  return new RegExp(`^${pattern}$`, 'u');
}

/** Whether a cached read belongs to the person or to the organization they were working in. */
export type ReadScope = 'user' | 'tenant';

export interface ReadPolicy {
  readonly pattern: RegExp;
  readonly scope: ReadScope;
  /** Groups entries in the device settings panel so an operator can see what is held locally. */
  readonly datasetKey: string;
}

/**
 * Reads that are copied to the device as they are fetched, and served back when the network is
 * gone. Every one of them is a whole-page read of the operator's own workspace — nothing here
 * is an administrative surface, an identity surface, or anything the assistant produces, all of
 * which need a live server to be answered honestly.
 */
export const cacheableReads: readonly ReadPolicy[] = [
  { pattern: route('/me'), scope: 'user', datasetKey: 'session' },
  { pattern: route('/tenants'), scope: 'user', datasetKey: 'session' },

  { pattern: route('/dashboard'), scope: 'tenant', datasetKey: 'dashboard' },

  { pattern: route('/fiscal-periods'), scope: 'tenant', datasetKey: 'scope' },
  { pattern: route('/currencies'), scope: 'tenant', datasetKey: 'scope' },
  { pattern: route('/currency-preferences'), scope: 'tenant', datasetKey: 'scope' },
  { pattern: route('/exchange-rates'), scope: 'tenant', datasetKey: 'exchangeRates' },

  { pattern: route('/accounts'), scope: 'tenant', datasetKey: 'accounts' },
  { pattern: route(`/accounts/${SEGMENT}`), scope: 'tenant', datasetKey: 'accounts' },
  { pattern: route(`/accounts/${SEGMENT}/ledger`), scope: 'tenant', datasetKey: 'ledger' },
  { pattern: route('/trial-balance'), scope: 'tenant', datasetKey: 'ledger' },

  { pattern: route('/persons'), scope: 'tenant', datasetKey: 'persons' },
  { pattern: route(`/persons/${SEGMENT}`), scope: 'tenant', datasetKey: 'persons' },
  { pattern: route(`/persons/${SEGMENT}/ledger`), scope: 'tenant', datasetKey: 'ledger' },

  { pattern: route('/journals'), scope: 'tenant', datasetKey: 'journals' },
  { pattern: route(`/journals/${SEGMENT}`), scope: 'tenant', datasetKey: 'journals' },

  { pattern: route('/products'), scope: 'tenant', datasetKey: 'inventory' },
  { pattern: route('/warehouses'), scope: 'tenant', datasetKey: 'inventory' },
  { pattern: route(`/inventory/${SEGMENT}`), scope: 'tenant', datasetKey: 'inventory' },

  { pattern: route('/customers'), scope: 'tenant', datasetKey: 'receivables' },
  { pattern: route('/customer-invoices'), scope: 'tenant', datasetKey: 'receivables' },
  { pattern: route('/customer-receipts'), scope: 'tenant', datasetKey: 'receivables' },
  { pattern: route('/customer-credit-notes'), scope: 'tenant', datasetKey: 'receivables' },
  { pattern: route('/receivable-write-offs'), scope: 'tenant', datasetKey: 'receivables' },
  { pattern: route('/receivable-collections'), scope: 'tenant', datasetKey: 'receivables' },
  { pattern: route(`/receivables/${SEGMENT}`), scope: 'tenant', datasetKey: 'receivables' },

  { pattern: route('/suppliers'), scope: 'tenant', datasetKey: 'payables' },
  { pattern: route('/supplier-bills'), scope: 'tenant', datasetKey: 'payables' },
  { pattern: route('/supplier-payments'), scope: 'tenant', datasetKey: 'payables' },
  { pattern: route('/payment-runs'), scope: 'tenant', datasetKey: 'payables' },
  { pattern: route(`/payables/${SEGMENT}`), scope: 'tenant', datasetKey: 'payables' },

  { pattern: route('/bank-accounts'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/cash-boxes'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/cash-boxes/limit-alerts'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/cash-transactions'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/deposit-batches'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/bank-statement-imports'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/bank-reconciliations'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/statement-mapping-profiles'), scope: 'tenant', datasetKey: 'treasury' },
  { pattern: route('/statement-journal-proposals'), scope: 'tenant', datasetKey: 'treasury' },

  { pattern: route('/reports/definitions'), scope: 'tenant', datasetKey: 'reports' },
];

/** Every dataset name a cached read can be filed under, for the device settings panel. */
export const cacheableDatasets: readonly string[] = [
  ...new Set(cacheableReads.map((policy) => policy.datasetKey)),
];

/** The path without its query string, which is what both allowlists match against. */
export function pathWithoutQuery(path: string): string {
  const [base = ''] = path.split('?');
  return base.replace(/\/+$/u, '') || '/';
}

export function readPolicyFor(path: string): ReadPolicy | null {
  const base = pathWithoutQuery(path);
  return cacheableReads.find((policy) => policy.pattern.test(base)) ?? null;
}

export interface WritePolicy {
  readonly method: 'POST' | 'PUT' | 'DELETE';
  readonly pattern: RegExp;
  /** Names the operation in the queue, in the sync centre, and in the audit trail. */
  readonly kind: string;
  readonly labelKey: string;
}

/**
 * Writes that may be captured on the device and replayed when the connection returns.
 *
 * This is deliberately the draft end of the General Ledger and nothing else. A draft is the one
 * document whose meaning does not depend on the state of the books at the moment it is written:
 * it takes the next number when the server accepts it, it touches no balance, and the server
 * still applies every rule it would have applied online. Posting, approving, reversing, paying
 * and receiving all decide something about the books as they stand, so they stay online-only
 * rather than being replayed against a ledger that has moved on since.
 */
export const queueableWrites: readonly WritePolicy[] = [
  { method: 'POST', pattern: route('/journals'), kind: 'journal.create', labelKey: 'offline.queue.journalCreate' },
  { method: 'PUT', pattern: route(`/journals/${SEGMENT}`), kind: 'journal.update', labelKey: 'offline.queue.journalUpdate' },
  { method: 'DELETE', pattern: route(`/journals/${SEGMENT}`), kind: 'journal.delete', labelKey: 'offline.queue.journalDelete' },
  { method: 'POST', pattern: route(`/journals/${SEGMENT}/submit`), kind: 'journal.submit', labelKey: 'offline.queue.journalSubmit' },
];

export function writePolicyFor(method: string, path: string): WritePolicy | null {
  const normalized = method.toUpperCase();
  const base = pathWithoutQuery(path);
  return queueableWrites.find((policy) => policy.method === normalized && policy.pattern.test(base)) ?? null;
}
